import { CircleCheck } from "lucide-react";
import React, { ReactNode } from "react";

import { Card, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Label } from "./ui/label";
import { RadioGroupItem } from "./ui/radio-group";
import { cn } from "@/lib/utils";

type IconType = React.ComponentType<{ className?: string }>;

export interface RadioCardProps {
  value: string;
  title: string;
  description: string;
  icon?: string | IconType;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  className?: string;
  children?: ReactNode;
}

interface IconProps {
  icon: RadioCardProps["icon"];
  selected: boolean;
  disabled: boolean;
}

function Icon({ icon, selected, disabled }: IconProps) {
  if (typeof icon === "string") {
    return (
      <img
        src={icon}
        className={cn(
          "size-16 transition-opacity duration-200",
          disabled && "opacity-50",
        )}
        alt=""
      />
    );
  } else {
    const IconComponent = icon as IconType;
    return (
      <IconComponent
        className={cn(
          "size-16 transition-colors duration-200",
          selected ? "text-primary" : "text-gray-600",
          disabled && "text-gray-400",
        )}
      />
    );
  }
}

export function RadioCard({
  value,
  title,
  description,
  icon,
  selected,
  onSelect,
  disabled = false,
  className,
  children,
}: RadioCardProps) {
  const cardId = `radio-card-${value}`;

  return (
    <div className={cn("relative", className)}>
      <RadioGroupItem
        id={cardId}
        value={value}
        className="peer sr-only"
        disabled={disabled}
        aria-describedby={`${cardId}-description`}
        data-testid={value}
      />
      <Label
        htmlFor={cardId}
        className={cn(
          "h-full block",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
        )}
      >
        <Card
          className={cn(
            "flex-row w-full h-full transition-all duration-200",
            "focus-within:outline-none focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2",
            !disabled && "hover:shadow-md hover:border-gray-300",
            selected && "border-primary ring-1 ring-primary",
            disabled && "opacity-50 hover:shadow-none hover:border-gray-200",
          )}
          tabIndex={disabled ? -1 : 0}
          aria-pressed={selected}
          aria-describedby={`${cardId}-description`}
          onClick={disabled ? undefined : onSelect}
          onKeyDown={(e) => {
            if (!disabled && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onSelect();
            }
          }}
        >
          <div
            className="flex items-center justify-center w-24 ml-6"
            aria-hidden="true"
          >
            {icon ? (
              <Icon icon={icon} selected={selected} disabled={disabled} />
            ) : null}
            {children}
          </div>
          <CardHeader className="flex-1">
            <CardTitle
              className={cn(
                "text-lg font-semibold",
                disabled && "text-gray-400",
              )}
              id={`${cardId}-title`}
            >
              {title}
            </CardTitle>
            <CardDescription
              className={cn(
                "text-sm text-gray-600 leading-relaxed",
                disabled && "text-gray-400",
              )}
              id={`${cardId}-description`}
            >
              {description}
            </CardDescription>
          </CardHeader>
        </Card>
        {selected && !disabled && (
          <CircleCheck
            className="absolute -top-2 -right-2 size-6 fill-primary text-white rounded-full bg-white shadow-sm"
            aria-hidden="true"
          />
        )}
      </Label>
    </div>
  );
}
